import { useDispatch } from "react-redux";
import { useCallback } from "react";
import styled from "styled-components";
import { Button } from "components";
import { Header } from "layout/ModalWindow/layout";
import { closeModal } from "layout";
import { PROCTOR_TELEGRAM_LINK, PROCTOR_ATTEMPTS_LIMIT } from "api/ProctorAPI";

interface ProctorAttemptsExhaustedModalProps {
	name: string;
	usedAttempts?: number;
}

const ProctorAttemptsExhaustedModal = ({ 
	usedAttempts = PROCTOR_ATTEMPTS_LIMIT 
}: ProctorAttemptsExhaustedModalProps) => {
	const dispatch = useDispatch();

	const handleClose = useCallback(() => {
		dispatch(closeModal("ProctorAttemptsExhaustedModal"));
	}, [dispatch]);

	return (
		<Container>
			<Header onClose={handleClose} />
			<Content>
				<IconWrapper>
					<Icon>🚫</Icon>
				</IconWrapper>
				
				<Title>Попытки исчерпаны</Title>
				
				<Description>
					К сожалению, вы использовали все доступные попытки 
					для сдачи экзамена с прокторингом.
				</Description>

				<InfoBlock>
					<InfoRow>
						<InfoLabel>Использовано попыток:</InfoLabel>
						<InfoValue highlight>
							{usedAttempts} из {PROCTOR_ATTEMPTS_LIMIT}
						</InfoValue>
					</InfoRow>
				</InfoBlock>

				<HelpText>
					Для получения дополнительных попыток свяжитесь с администратором 
					через Telegram. Менеджер сбросит счётчик попыток после обработки 
					вашего запроса.
				</HelpText>

				<ButtonsContainer>
					<TelegramButton
						href={PROCTOR_TELEGRAM_LINK}
						target="_blank"
						rel="noopener noreferrer"
					>
						Получить ещё попытки
					</TelegramButton>
					<Button buttonTheme="secondary" onClick={handleClose}>
						Закрыть
					</Button>
				</ButtonsContainer>

				<ContactInfo>
					Или напишите нам на почту:{" "}
					<ContactLink href="mailto:support@pddtv.ru">
						support@pddtv.ru
					</ContactLink>
				</ContactInfo>
			</Content>
		</Container>
	);
};

export default ProctorAttemptsExhaustedModal;

const Container = styled.div`
	background: #fff;
	border-radius: 16px;
	max-width: 480px;
	width: 100%;
	padding-top: 16px;
`;

const Content = styled.div`
	padding: 0 32px 32px;
	text-align: center;
`;

const IconWrapper = styled.div`
	margin-bottom: 16px;
`;

const Icon = styled.span`
	font-size: 48px;
`;

const Title = styled.h2`
	font-size: 1.5rem;
	font-weight: 600;
	margin: 0 0 12px;
	color: ${({ theme }) => theme.colors.secondaryRed};
`;

const Description = styled.p`
	color: ${({ theme }) => theme.colors.secondaryGrey};
	line-height: 1.6;
	margin-bottom: 24px;
`;

const InfoBlock = styled.div`
	background: ${({ theme }) => theme.colors.opacityLightGrey10};
	border-radius: 12px;
	padding: 16px 20px;
	margin-bottom: 20px;
`;

const InfoRow = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 8px 0;

	&:not(:last-child) {
		border-bottom: 1px solid ${({ theme }) => theme.colors.secondaryLightGray};
	}
`;

const InfoLabel = styled.span`
	color: ${({ theme }) => theme.colors.secondaryGrey};
	font-size: 0.95rem;
`;

const InfoValue = styled.span<{ highlight?: boolean }>`
	font-weight: 600;
	color: ${({ highlight, theme }) => 
		highlight ? theme.colors.secondaryRed : theme.colors.primaryBlack};
	font-size: ${({ highlight }) => (highlight ? "1.1rem" : "0.95rem")};
`;

const HelpText = styled.p`
	font-size: 0.9rem;
	color: ${({ theme }) => theme.colors.secondaryGrey};
	line-height: 1.5;
	margin-bottom: 24px;
`;

const ButtonsContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	margin-bottom: 16px;
`;

const TelegramButton = styled.a`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 48px;
	background: ${({ theme }) => theme.colors.primaryGreen};
	color: #fff;
	border-radius: 8px;
	font-size: 1rem;
	font-weight: 600;
	text-decoration: none;
	transition: background 0.2s;

	&:hover {
		background: ${({ theme }) => theme.colors.primaryGreenHover};
	}
`;

const ContactInfo = styled.p`
	font-size: 0.85rem;
	color: ${({ theme }) => theme.colors.secondaryGrey};
`;

const ContactLink = styled.a`
	color: ${({ theme }) => theme.colors.primaryGreen};
	text-decoration: none;

	&:hover {
		text-decoration: underline;
	}
`;






